/**
 * reservationsV2Selectors — todo lo que la ficha y el listado de Reservas V2
 * muestran, derivado en funciones puras.
 *
 * Reglas que se siguen aquí:
 *   1. Reserva ≠ huésped ≠ unidad física. Una reserva existe sobre un TIPO de
 *      alojamiento y puede no tener todavía unidad asignada (§6.1).
 *   2. Los tres ejes de estado (reserva, estancia, pago) son independientes y
 *      nada de este módulo deduce uno a partir de otro.
 *   3. Nada se declara dos veces: las reservas salen del dataset PMS, las
 *      peticiones y servicios de `todayV2Store`, y el vocabulario, los
 *      horarios, los servicios y los impuestos de Ajustes V2.
 *   4. Lo que no está en los datos se DERIVA con una regla escrita o se
 *      declara ausente. Nunca se inventa: un contacto compuesto, un importe
 *      de depósito supuesto o un reporte de inmigración deducido del check-in
 *      son datos falsos, y un dato falso en operaciones se usa como si fuera
 *      cierto.
 *   5. Aquí solo cabe la economía DE UNA RESERVA. Los gastos del periodo, la
 *      comisión de gestión y el reparto al propietario son de Finanzas.
 *
 * Sin estado propio, sin efectos, sin red.
 */
import { DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import {
  getVilla, getChannel, blocksForVilla, isDayInBlock, datesBetweenExclusive,
  nightsBetween, addDays,
} from "@/lib/pmsSelectors";
import { DEPARTURE_DETAILS } from "@/data/todayV2Demo";
import {
  buildGuestParty, guestsForReservation, contactOf, asksTransfer, transferSummaryOf,
} from "@/lib/guestsV2Selectors";
import { arrivalModeOf } from "@/data/guestsV2Demo";
import {
  RESERVATION_EXTRAS, PAYMENT_EVENT_KINDS,
  EXTERNAL_REF_PREFIX, CHANNEL_MANAGER_CHANNELS,
} from "@/data/reservationsV2Demo";

/** Ventana en la que un aviso de llegada deja de ser ruido y pasa a ser trabajo. */
const ATTENTION_WINDOW_DAYS = 3;

// ---------------------------------------------------------------------------
// Tipo de alojamiento y unidad física
// ---------------------------------------------------------------------------

/**
 * Lo que el huésped compró. En el dataset V1 el campo se llama `pool_id` por
 * razones históricas, pero lo que identifica es la categoría comercial: el
 * tipo de alojamiento (§2.1).
 */
export function unitTypeFor(reservation, settings) {
  const id = reservation?.pool_id;
  return (settings.unitTypes || []).find((t) => t.unit_type_id === id) || null;
}

/** Grupo de reparto que aplica a ese tipo. Para Dolce hay uno por tipo (1:1). */
export function poolFor(reservation, settings) {
  const typeId = reservation?.pool_id;
  return (settings.pools || []).find((p) => p.unit_type_id === typeId) || null;
}

/**
 * Unidades que PUEDEN alojar esta reserva: las del tipo contratado y nada más.
 * Una Dolce Vistas no se asigna jamás a una Dolce Privè, ni al revés.
 */
export function unitsOfType(reservation, settings) {
  const typeId = reservation?.pool_id;
  return (settings.units || []).filter((u) => u.unit_type_id === typeId);
}

/** La unidad física asignada, o `null` si todavía no tiene. */
export function assignedUnitFor(reservation) {
  return reservation?.assigned_villa_id ? getVilla(reservation.assigned_villa_id) || null : null;
}

/**
 * ¿La unidad es elegible para esta reserva? Se comprueba SIEMPRE antes de
 * asignar: es la salvaguarda de que el tipo manda sobre la unidad.
 */
export function isUnitEligible(reservation, unitId, settings) {
  return unitsOfType(reservation, settings).some((u) => u.unit_id === unitId);
}

/** Bloqueo de mantenimiento que solape la estancia de la unidad asignada. */
export function blockingMaintenanceFor(reservation) {
  if (!reservation?.assigned_villa_id) return null;
  const blocks = blocksForVilla(reservation.assigned_villa_id)
    .filter((b) => b.type === "MAINTENANCE");
  if (blocks.length === 0) return null;
  const nights = datesBetweenExclusive(reservation.check_in, reservation.check_out);
  for (const iso of nights) {
    const hit = blocks.find((b) => isDayInBlock(iso, b));
    if (hit) return hit;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Huésped
// ---------------------------------------------------------------------------

/**
 * Contacto del huésped. SOLO lo declarado, y de UN solo sitio.
 *
 * Esto ya no declara nada: DERIVA del modelo de huésped, que es donde vive la
 * persona con su `guest_id`. Antes el mismo email estaba escrito dos veces
 * —aquí en `RESERVATION_EXTRAS.guest` y allí en `GUESTS`— y dos copias de un
 * dato son dos datos: se corrige una, se olvida la otra, y la ficha de la
 * reserva acaba enseñando un teléfono distinto del que enseña Huéspedes de la
 * misma persona. Se conserva la función porque la búsqueda de reservas todavía
 * la consume.
 *
 * Si no consta email o teléfono, el campo se queda vacío y la ficha lo dice. No
 * se compone ninguna dirección: un contacto inventado parece operativo, alguien
 * lo copia y escribe a una dirección que no existe.
 *
 * `nationality` sí puede caer a `guest.country`, que es un dato declarado de la
 * propia reserva, no una composición.
 */
export function guestContactFor(reservation, guestState = null) {
  const guests = guestsForReservation(reservation.reservation_id, guestState);
  return contactOf(guests, reservation);
}



/**
 * Preparación del huésped y reporte de inmigración.
 *
 * Esta ficha ya NO calcula nada de esto: lo deriva el modelo de huésped
 * (`guestsV2Selectors`), que es donde viven las personas de la reserva. Antes
 * había dos cuentas distintas —una aquí y otra en Hoy— sobre los mismos datos,
 * que es justo la forma de que dos pantallas se contradigan.
 *
 * Se conserva la forma que ya consumían los componentes (`pre_checkin`,
 * `missing`, `immigration`…) para no arrastrar el cambio a media ficha.
 */
export function readinessFor(reservation, settings, guestState) {
  const party = buildGuestParty({ reservation, settings, storeState: guestState });
  const required = party.required.all;
  return {
    pre_checkin: party.pre_checkin,
    fields: required,
    missing: party.missing,
    completed: Math.max(0, required.length - new Set(party.missing.map((m) => m.field)).size),
    immigration: party.immigration.state,
    immigration_tracked: party.immigration.tracked,
    immigration_declared: party.immigration.declared,
    party,
  };
}

// ---------------------------------------------------------------------------
// Canal
// ---------------------------------------------------------------------------

/**
 * Referencia del canal. Cuando exista channel manager llegará del proveedor;
 * aquí se compone para poder enseñar el campo. Ningún proveedor está
 * conectado.
 */
export function externalRefFor(reservation) {
  const declared = RESERVATION_EXTRAS[reservation.reservation_id];
  if (declared && "external_ref" in declared) return declared.external_ref;
  const prefix = EXTERNAL_REF_PREFIX[reservation.channel];
  if (!prefix) return null;
  return `${prefix}-${String(reservation.reservation_id).replace(/\D/g, "").slice(-7)}`;
}

/** ¿Este canal entrará por un channel manager en el futuro? */
export function viaChannelManager(reservation) {
  return CHANNEL_MANAGER_CHANNELS.includes(reservation.channel);
}

// ---------------------------------------------------------------------------
// Estancia
// ---------------------------------------------------------------------------

/**
 * La estancia, con la llegada DERIVADA del modelo de huésped.
 *
 * `eta` es la hora prevista de llegada A LA PROPIEDAD, que es la que decide
 * cuándo hay que tener la unidad lista y a alguien esperando. La hora de
 * aterrizaje viaja aparte en `landing_time`: son dos hechos distintos y aquí
 * se declaraban como uno solo, con el resultado de que la ficha enseñaba el
 * aterrizaje como si fuera la llegada.
 *
 * La salida tardía sí sigue declarada: es un servicio pedido y resuelto que no
 * modela ningún otro sitio (§9).
 */
export function stayFor(reservation, settings, guestState) {
  const party = buildGuestParty({ reservation, settings, storeState: guestState });
  const arrival = party.arrival;
  const mode = arrivalModeOf(arrival?.mode);
  const departure = DEPARTURE_DETAILS[reservation.reservation_id] || {};
  return {
    check_in: reservation.check_in,
    check_out: reservation.check_out,
    nights: nightsBetween(reservation.check_in, reservation.check_out),
    check_in_time: settings.property.check_in_time,
    check_out_time: settings.property.check_out_time,
    eta: arrival?.estimated_arrival || null,
    arrival_mode: arrival?.mode || null,
    arrival_mode_label: arrival ? mode.label : null,
    arrival_reference: mode.ref_label ? arrival?.reference || null : null,
    landing_time: mode.time_label ? arrival?.landing_time || null : null,
    landing_label: mode.time_label,
    // Quien llega por su cuenta no tiene traslado que confirmar: `null`, no
    // "sin indicar".
    transfer: asksTransfer(arrival) ? arrival?.transfer || null : null,
    transfer_label: transferSummaryOf(arrival),
    late_checkout: departure.late_checkout || null,
    early_check_in: null,
  };
}

// ---------------------------------------------------------------------------
// Servicios y peticiones
// ---------------------------------------------------------------------------

/**
 * Pedidos de servicio de esta reserva. El precio NO se guarda en el pedido:
 * sale del catálogo de Ajustes, que es donde se configura una vez.
 */
export function servicesFor(reservation, requests) {
  return requests
    .filter((r) => r.reservation_id === reservation.reservation_id && r.creates === "service_order")
    .map((r) => {
      const unitPrice = r.service?.sale_price_eur || 0;
      const quantity = Number(r.quantity || 1);
      return {
        ...r,
        unit_price_eur: unitPrice,
        quantity,
        line_total_eur: unitPrice * quantity,
      };
    });
}

/** Peticiones operativas (limpieza, avisos) ligadas a esta reserva. */
export function operationalRequestsFor(reservation, requests) {
  return requests.filter(
    (r) => r.reservation_id === reservation.reservation_id && r.creates !== "service_order",
  );
}

// ---------------------------------------------------------------------------
// Dinero (resumen de reserva — NO es contabilidad)
// ---------------------------------------------------------------------------

function paymentMethodFor(reservation) {
  if (getChannel(reservation.channel)?.kind === "ota") return "channel";
  return reservation.payment_method || "other";
}

/**
 * Eventos de pago. Los que la reserva declara mandan; si no hay ninguno se
 * derivan del estado de pago SOLO cuando ese estado fija el importe sin
 * ambigüedad: "pagada" son el total, "reembolsada" el total y su devolución.
 *
 * "Parcialmente pagada" NO lo fija: dice que entró algo, no cuánto. Repartirlo
 * con un porcentaje de depósito supuesto sería inventarse dinero cobrado, así
 * que en ese caso no se emite ningún movimiento y la ficha dice que el importe
 * no está disponible. Si una reserva de demostración tiene que enseñar un pago
 * parcial, su movimiento se declara en `RESERVATION_EXTRAS`.
 */
export function paymentEventsFor(reservation, storeState) {
  const session = storeState.paymentEvents[reservation.reservation_id];
  if (session && session.length > 0) return session;

  const amount = reservation.accommodation_revenue_eur || 0;
  const method = paymentMethodFor(reservation);
  const at = reservation.created_at || null;
  const base = { method, at, derived: true, origin: "derived" };

  switch (reservation.payment_status) {
    case "paid":
      return [{ event_id: `PE-${reservation.reservation_id}-D1`, kind: "final", amount_eur: amount, note: "Derivado del estado de pago", ...base }];
    case "partially_paid":
      // Se sabe que hay un cobro parcial; no se sabe de cuánto. Sin movimiento
      // declarado, el importe no existe y no se fabrica.
      return [];
    case "partially_refunded":
      return [
        { event_id: `PE-${reservation.reservation_id}-D1`, kind: "final", amount_eur: amount, note: "Derivado del estado de pago", ...base },
        {
          event_id: `PE-${reservation.reservation_id}-D2`,
          kind: "refund",
          amount_eur: Number(reservation.cancellation?.refunded_eur || 0),
          note: "Reembolso parcial",
          ...base,
        },
      ];
    case "refunded":
      return [
        { event_id: `PE-${reservation.reservation_id}-D1`, kind: "final", amount_eur: amount, note: "Derivado del estado de pago", ...base },
        { event_id: `PE-${reservation.reservation_id}-D2`, kind: "refund", amount_eur: amount, note: "Reembolso total", ...base },
      ];
    default:
      // unpaid · pending · authorized · failed → todavía no hay cobro.
      return [];
  }
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Parte de impuesto CONTENIDA en un importe que ya lo lleva dentro.
 *
 * Si el precio bruto incluye un 10 %, el impuesto no es el 10 % del bruto: el
 * bruto es el 110 % de la base, así que la parte de impuesto es
 * bruto × tipo / (100 + tipo). Con 110 al 10 % son 10, no 11.
 */
export function taxIncludedIn(gross, ratePct) {
  const rate = Number(ratePct) || 0;
  if (rate <= 0) return 0;
  return round2(((Number(gross) || 0) * rate) / (100 + rate));
}

/**
 * Moneda en la que están DENOMINADOS los importes del dataset de demostración.
 *
 * No es una preferencia de visualización: es un hecho sobre el dato. Todos los
 * campos monetarios de la demo se llaman `*_eur` y contienen euros
 * (`accommodation_revenue_eur`, `sale_price_eur`, `amount_eur`,
 * `refunded_eur`…). 1.400 de `accommodation_revenue_eur` son 1.400 €, y lo
 * siguen siendo pase lo que pase en Ajustes.
 */
export const DATASET_CURRENCY = "EUR";

/**
 * Forma explícita del dinero: importe y moneda SIEMPRE juntos.
 *
 * Un número suelto no es una cantidad de dinero; solo lo es acompañado de su
 * moneda. Separarlos es lo que permite que alguien reetiquete 1.400 € como
 * 1.400 $ sin convertir nada, que es exactamente el error que esta forma
 * existe para impedir. Hacia esto va el modelo de producción; aquí se usa en
 * el resultado de la reserva sin rediseñar el resto del PMS.
 */
export function moneyValue(amount, currency = DATASET_CURRENCY) {
  return { amount: Number(amount) || 0, currency };
}

/**
 * Monedas CONFIGURADAS de la propiedad. Esto es metadato de configuración, y
 * nada más.
 *
 *   - `operational`: la del día a día de la propiedad (cobros, proveedores,
 *     caja). En Dolce, IDR.
 *   - `reporting`: la de informes y vistas de inversor. En Dolce, EUR.
 *
 * Deliberadamente NO devuelve ninguna moneda "de visualización". Cambiar
 * `currency_reporting` a USD no convierte nada ni puede reetiquetar un importe
 * que está en euros: un dato en EUR enseñado como "$" es un dato financiero
 * falso, del mismo tipo que un depósito inventado. Cuando la moneda de
 * informes no coincide con la del dato, la respuesta honesta es que ese
 * importe no existe en esa moneda —no repintarle el símbolo—, así que el
 * importe se queda en la suya y la pantalla lo dice.
 *
 * No hay conversión de divisa en ninguna parte de la vista previa. Cuando la
 * operativa real en IDR exista, traerá su propio importe en IDR, no uno
 * derivado de este.
 */
export function currencyFor(settings) {
  const reporting = settings.property?.currency_reporting
    || settings.finance?.currency_reporting
    || null;
  const operational = settings.property?.currency_operational
    || settings.finance?.currency_operational
    || null;
  return {
    operational,
    reporting,
    // Moneda real de los importes que se enseñan. Es la del dato, no la de
    // Ajustes, y por eso es una constante y no una lectura de configuración.
    dataset: DATASET_CURRENCY,
    // ¿Existe el importe en la moneda de informes? Solo si es la misma.
    reporting_available: reporting === DATASET_CURRENCY,
    converted: false,
  };
}

/**
 * Resumen de cobro de la reserva. El precio de alojamiento ya incluye el
 * impuesto de alojamiento (tipo y base configurados en Ajustes), así que el
 * impuesto se muestra como "del cual" y no se suma otra vez.
 *
 * Esto NO es contabilidad ni resultado: para eso está `economicsFor`, y para
 * el resultado del periodo, Finanzas.
 */
export function moneyFor({ reservation, services, settings, events }) {
  const accommodation = reservation.accommodation_revenue_eur || 0;
  const servicesTotal = services.reduce((acc, s) => acc + s.line_total_eur, 0);
  const discount = 0;
  const total = accommodation + servicesTotal - discount;
  const taxPct = Number(settings.finance?.tax_pct || 0);

  const paid = events.reduce((acc, e) => {
    const sign = PAYMENT_EVENT_KINDS[e.kind]?.sign ?? 1;
    return acc + sign * Number(e.amount_eur || 0);
  }, 0);

  // Cobro parcial sin ningún movimiento: consta que entró algo, pero no cuánto.
  // El pendiente se calcula igual sobre lo conocido —así el aviso de saldo y el
  // filtro "pendiente de cobro" siguen funcionando— y la ficha avisa de que la
  // cifra cobrada no está completa.
  const partialUnknown = reservation.payment_status === "partially_paid"
    && events.length === 0;

  return {
    accommodation_eur: accommodation,
    accommodation_tax_inclusive: true,
    services_eur: servicesTotal,
    discount_eur: discount,
    total_eur: total,
    tax_pct: taxPct,
    tax_included_eur: taxIncludedIn(accommodation, taxPct),
    paid_eur: paid,
    outstanding_eur: round2(total - paid),
    amount_paid_known: !partialUnknown,
    partial_amount_unknown: partialUnknown,
    // ÚNICA moneda de este objeto, y es la del DATO: todos sus importes salen
    // de campos `*_eur`. Cambiar Ajustes no reetiqueta un euro como dólar.
    //
    // Las monedas configuradas de la propiedad NO viven aquí a propósito. Si
    // estuvieran, `money.currency_reporting` quedaría a un carácter de
    // distancia de `money.currency` en cada llamada al formateador, y esa
    // confusión es exactamente el fallo que este módulo tiene que impedir.
    // Son configuración: se leen con `currencyFor(settings)`.
    currency: DATASET_CURRENCY,
    has_derived_events: events.some((e) => e.derived),
  };
}

/**
 * Coste del canal de venta, leído de Ajustes › Finanzas.
 *
 * El porcentaje NO se escribe en ningún componente: sale de
 * `finance.channel_costs`, que es donde se configura una vez, y su campo
 * `approved` distingue lo decidido de lo que todavía es un supuesto de
 * modelado.
 *
 * Una reserva directa no lleva comisión de OTA. Si el canal directo tiene un
 * fee configurado (la web propia, por ejemplo), se enseña con su etiqueta
 * real: es un coste de canal directo, no una comisión de OTA.
 */
export function channelCostFor(reservation, settings) {
  const channel = getChannel(reservation.channel);
  const configured = (settings.finance?.channel_costs || [])
    .find((c) => c.key === reservation.channel);
  if (!configured || !Number(configured.pct)) return null;
  return {
    key: configured.key,
    label: configured.label,
    pct: Number(configured.pct),
    approved: Boolean(configured.approved),
    is_ota: channel?.kind === "ota",
  };
}

/**
 * Resultado de UNA reserva. Solo llegan hasta aquí los conceptos que de verdad
 * pertenecen a una estancia:
 *
 *   ingreso bruto de alojamiento
 *   + servicios de la reserva
 *   − PBJT / impuesto de alojamiento (ya incluido en el bruto)
 *   − coste de canal cuando aplica
 *   − procesamiento de pago cuando se cobra por nuestra pasarela
 *   = ingreso neto de la reserva
 *
 * Lo que NO se calcula aquí, ni se estima, ni se reparte: sueldos, suministros,
 * mantenimiento general, seguros, cualquier otro gasto operativo, la comisión
 * de gestión y el beneficio del propietario. Son costes de un periodo, no de
 * una estancia, y su reparto se decide en Finanzas · Cierre mensual. La
 * comisión de gestión, además, sigue sin fijarse.
 */
export function economicsFor({ reservation, settings, money }) {
  const channel = getChannel(reservation.channel);
  const viaOta = channel?.kind === "ota";

  const tax = {
    pct: money.tax_pct,
    amount: moneyValue(money.tax_included_eur, money.currency),
    label: "PBJT · impuesto de alojamiento",
    base_label: settings.finance?.tax_base || "Ingresos de alojamiento",
    inclusive: true,
  };

  const configured = channelCostFor(reservation, settings);
  const channelCost = configured
    ? {
      ...configured,
      amount: moneyValue(round2((money.accommodation_eur * configured.pct) / 100), money.currency),
    }
    : null;

  // Las OTA cobran al huésped por su cuenta: no pasan por nuestra pasarela. El
  // coste fijo por transacción está configurado en moneda operativa y aquí no
  // se convierte, así que solo se aplica el porcentaje y se dice.
  const processingPct = Number(settings.finance?.payment_processing_pct || 0);
  const processing = !viaOta && processingPct > 0
    && (reservation.payment_method === "card")
    ? {
      pct: processingPct,
      amount: moneyValue(round2((money.total_eur * processingPct) / 100), money.currency),
      fixed_excluded: Boolean(settings.finance?.payment_processing_fixed_idr),
    }
    : null;

  // Todas las líneas están en la misma moneda —la del dato—, así que restarlas
  // es legítimo. Nunca se mezclan monedas ni se convierte ninguna.
  const net = round2(
    money.total_eur - tax.amount.amount
    - (channelCost?.amount.amount || 0)
    - (processing?.amount.amount || 0),
  );

  return {
    accommodation: moneyValue(money.accommodation_eur, money.currency),
    services: moneyValue(money.services_eur, money.currency),
    gross: moneyValue(money.total_eur, money.currency),
    tax,
    channel_cost: channelCost,
    processing,
    net_revenue: moneyValue(net, money.currency),
    currency: money.currency,
  };
}

// ---------------------------------------------------------------------------
// Mensajes y actividad
// ---------------------------------------------------------------------------

export function messagesFor(reservation) {
  return (RESERVATION_EXTRAS[reservation.reservation_id]?.messages || [])
    .slice()
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function notesFor(reservation) {
  return RESERVATION_EXTRAS[reservation.reservation_id]?.notes || null;
}

/**
 * Registro de actividad. La base se DERIVA de lo que la propia reserva ya
 * sabe (cuándo se creó, si hubo asignación manual, si se canceló) y encima se
 * apila lo que se haya hecho en la vista previa.
 */
export function activityFor(reservation, storeState) {
  const out = [];
  if (reservation.created_at) {
    out.push({
      entry_id: `AC-${reservation.reservation_id}-created`,
      kind: "created",
      at: reservation.created_at,
      detail: `Canal ${getChannel(reservation.channel)?.label || reservation.channel}`,
      actor: "Sistema",
    });
  }
  if (reservation.allocation?.manual_override?.at) {
    out.push({
      entry_id: `AC-${reservation.reservation_id}-override`,
      kind: "override",
      at: reservation.allocation.manual_override.at,
      detail: reservation.allocation.manual_override.reason,
      actor: reservation.allocation.manual_override.actor_name || "Equipo",
    });
  } else if (reservation.allocation && reservation.assigned_villa_id) {
    out.push({
      entry_id: `AC-${reservation.reservation_id}-assigned`,
      kind: "assigned",
      at: reservation.created_at,
      detail: `${reservation.assigned_villa_id} · asignación automática`,
      actor: "Motor de reparto",
    });
  }
  if (reservation.cancellation?.cancelled_at) {
    out.push({
      entry_id: `AC-${reservation.reservation_id}-cancelled`,
      kind: "cancelled",
      at: reservation.cancellation.cancelled_at,
      detail: reservation.cancellation.reason || null,
      actor: reservation.cancellation.cancelled_by || "—",
    });
  }
  const session = storeState.activity[reservation.reservation_id] || [];
  return [...out, ...session].sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
}

// ---------------------------------------------------------------------------
// Necesita atención
// ---------------------------------------------------------------------------

/**
 * Excepciones operativas de una reserva. Es una REPRESENTACIÓN de demo: con
 * backend, lo accionable lo emite el Action Center (§5.2, §16.1) y esta
 * pantalla lo consume, no lo calcula.
 */
export function attentionFor({ reservation, assignedUnit, readiness, money, requests, today = DEMO_TODAY_ISO }) {
  const out = [];
  if (reservation.booking_status === "cancelled") return out;

  const soon = reservation.check_in <= addDays(today, ATTENTION_WINDOW_DAYS);
  const future = reservation.check_out >= today;

  if (!assignedUnit && future) {
    out.push({ key: "unassigned", label: "Sin unidad asignada", tone: "red" });
  }

  const blocking = blockingMaintenanceFor(reservation);
  if (blocking && future) {
    out.push({ key: "unit_blocked", label: "Unidad bloqueada por mantenimiento", tone: "red" });
  }

  if (soon && future && readiness.pre_checkin !== "completed") {
    out.push({ key: "pre_checkin", label: "Check-in online incompleto", tone: "amber" });
  }
  if (soon && future && readiness.immigration_tracked && readiness.immigration === "due") {
    out.push({ key: "immigration", label: "Inmigración pendiente", tone: "amber" });
  }
  if (soon && money.outstanding_eur > 0) {
    out.push({ key: "balance", label: "Saldo pendiente", tone: "amber" });
  }

  const transfer = requests.find((r) => r.type === "airport_pickup" && r.status === "requested");
  if (transfer) {
    out.push({ key: "transfer", label: "Traslado sin confirmar", tone: "amber" });
  }
  const pendingRequest = requests.find((r) => r.status === "requested" && r.type !== "airport_pickup");
  if (pendingRequest) {
    out.push({ key: "request", label: "Petición del huésped sin atender", tone: "amber" });
  }

  return out;
}

// ---------------------------------------------------------------------------
// Composición
// ---------------------------------------------------------------------------

/**
 * Vista completa de una reserva. La usan el listado y la ficha, así que una
 * fila y su detalle no pueden contradecirse.
 */
export function buildReservationView({
  reservation, settings, requests, storeState, guestState, today = DEMO_TODAY_ISO,
}) {
  const unitType = unitTypeFor(reservation, settings);
  const assignedUnit = assignedUnitFor(reservation);
  const services = servicesFor(reservation, requests);
  const operationalRequests = operationalRequestsFor(reservation, requests);
  const allRequests = requests.filter((r) => r.reservation_id === reservation.reservation_id);
  const events = paymentEventsFor(reservation, storeState);
  const money = moneyFor({ reservation, services, settings, events });
  const economics = economicsFor({ reservation, settings, money });
  const readiness = readinessFor(reservation, settings, guestState);
  const attention = attentionFor({
    reservation, assignedUnit, readiness, money, requests: allRequests, today,
  });

  return {
    reservation,
    id: reservation.reservation_id,
    unitType,
    pool: poolFor(reservation, settings),
    assignedUnit,
    eligibleUnits: unitsOfType(reservation, settings),
    blockingMaintenance: blockingMaintenanceFor(reservation),
    channel: getChannel(reservation.channel),
    viaChannelManager: viaChannelManager(reservation),
    externalRef: externalRefFor(reservation),
    guestContact: guestContactFor(reservation, guestState),
    readiness,
    stay: stayFor(reservation, settings, guestState),
    services,
    operationalRequests,
    events,
    money,
    economics,
    messages: messagesFor(reservation),
    notes: notesFor(reservation),
    activity: activityFor(reservation, storeState),
    attention,
    segment: segmentOf(reservation, today),
  };
}

/** Dónde cae la reserva respecto a hoy. Deriva; no es un campo guardado. */
export function segmentOf(reservation, today = DEMO_TODAY_ISO) {
  if (reservation.booking_status === "cancelled") return "cancelled";
  if (reservation.check_in === today) return "arrival";
  if (reservation.check_out === today) return "departure";
  if (reservation.check_in > today) return "upcoming";
  if (reservation.check_out < today) return "past";
  return "in_house";
}

// ---------------------------------------------------------------------------
// Búsqueda y filtros
// ---------------------------------------------------------------------------

function matchesQuery(view, q) {
  if (!q) return true;
  const r = view.reservation;
  const unit = view.assignedUnit;
  const hay = [
    r.reservation_id,
    r.guest?.name,
    unit?.villa_id,
    unit?.name,
    view.unitType?.name,
    view.externalRef,
    view.guestContact?.email,
    view.guestContact?.phone,
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q);
}

function matchesQuick(view, quick, today) {
  const r = view.reservation;
  switch (quick) {
    case "today":
      return view.segment === "arrival" || view.segment === "departure" || view.segment === "in_house";
    case "arrivals": return view.segment === "arrival";
    case "departures": return view.segment === "departure";
    case "in_house": return view.segment === "in_house";
    case "upcoming": return view.segment === "upcoming";
    case "unassigned": return !view.assignedUnit && r.booking_status !== "cancelled";
    case "unpaid": return view.money.outstanding_eur > 0 && r.booking_status !== "cancelled";
    case "attention": return view.attention.length > 0;
    case "cancelled": return r.booking_status === "cancelled";
    default: return true;
  }
}

/**
 * Filtrado del listado. Las canceladas NO se esconden por defecto salvo que
 * el usuario esté mirando otra cosa: se conservan, se buscan y se informan.
 */
export function filterReservationViews({ views, query = "", quick = null, filters = {}, today = DEMO_TODAY_ISO }) {
  const q = query.trim().toLowerCase();
  return views.filter((view) => {
    const r = view.reservation;
    if (quick && !matchesQuick(view, quick, today)) return false;
    if (filters.booking_status && filters.booking_status !== "all"
      && r.booking_status !== filters.booking_status) return false;
    if (filters.stay_status && filters.stay_status !== "all"
      && r.stay_status !== filters.stay_status) return false;
    if (filters.payment_status && filters.payment_status !== "all"
      && r.payment_status !== filters.payment_status) return false;
    if (filters.channel && filters.channel !== "all" && r.channel !== filters.channel) return false;
    if (filters.unit_type && filters.unit_type !== "all" && r.pool_id !== filters.unit_type) return false;
    if (filters.unit && filters.unit !== "all") {
      if (filters.unit === "none" ? Boolean(r.assigned_villa_id) : r.assigned_villa_id !== filters.unit) {
        return false;
      }
    }
    if (filters.from && r.check_out < filters.from) return false;
    if (filters.to && r.check_in > filters.to) return false;
    if (!matchesQuery(view, q)) return false;
    return true;
  });
}

export function quickFilterCounts(views, today = DEMO_TODAY_ISO) {
  const counts = {};
  for (const key of [
    "today", "arrivals", "departures", "in_house", "upcoming",
    "unassigned", "unpaid", "attention", "cancelled",
  ]) {
    counts[key] = views.filter((v) => matchesQuick(v, key, today)).length;
  }
  return counts;
}
